import { makeRng } from './rng';
import {
  WEAPONS,
  ARMOR_HP_REDUCTION,
  ARMOR_DEGRADE,
  ARMOR_DEGRADE_FLAT,
  CRIT_ARMOR_PIERCE,
  maxHpOf,
  armorPointsOf,
  STANCE,
  KNIFE,
  FLASH_ACCURACY,
  FLASH_SPLASH_MAX,
  ADRENALINE_PER_GAP,
  ADRENALINE_MAX_FRAC,
  COMEBACK_HP_FLOOR,
  FOCUS_RESIST_K,
  FOCUS_RESIST_CAP,
  type Stance,
} from './config';
import type { FighterBase, FighterInit, Loadout, Team, WeaponClass } from './types';

const Y_TOP = 420;
const Y_BOTTOM = 1380;

export const GRENADE_DAMAGE = 42;
export const GRENADE_SPLASH = 0.3;
const GRENADE_SPLASH_MAX = 1;
const GRENADE_FLIGHT_TICKS = 50;
const GRENADE_SCATTER: [number, number][] = [
  [0.3, 0.8],
  [0.75, 0.58],
  [1.0, 0.4],
];
const grenadeScatter = (roll: number): number => {
  for (const [p, f] of GRENADE_SCATTER) if (roll < p) return f;
  return GRENADE_SCATTER[GRENADE_SCATTER.length - 1][1];
};

export const GRENADE_VS_STANCE: Record<Stance, number> = { attack: 1, defense: 1.35, maneuver: 0.8 };
export const SMOKE_SHIELD_ROUNDS = 2;
export const SMOKE_MIT = 0.5;
export const FLASH_BLIND_ROUNDS = 2;
export const MEDKIT_HEAL_FRAC = 0.45;
export const MEDKIT_HEAL_FLOOR = 25;

const SEG_TICKS = 28;
const MIN_SEGMENTS = 4;
const MAX_SEGMENTS = 7;

function missSpreadOk(arr: number[], positives: number): boolean {
  const n = arr.length;
  const miss = (i: number) => arr[i] >= positives;
  if (n >= 2 && miss(0) && miss(1)) return false;
  if (n >= 2 && miss(n - 1) && miss(n - 2)) return false;
  let run = 0;
  for (let i = 0; i < n; i++) {
    run = miss(i) ? run + 1 : 0;
    if (run >= 3) return false;
  }
  return true;
}

// WHY: seed is the MATCH seed, not per-round; the reject loop reshuffles the SAME rng — re-seeding replays the rejected
// permutation forever
function bagRoll(seed: string, fighterId: string, kind: 'acc' | 'crit', counter: number, positives: number, size: number): boolean {
  const block = Math.floor(counter / size);
  const idx = counter % size;
  const rng = makeRng(`${seed}:bag:${kind}:${fighterId}:${block}`);
  const arr = Array.from({ length: size }, (_, i) => i);
  const shuffle = () => {
    for (let i = size - 1; i > 0; i--) { const j = rng.int(i + 1); [arr[i], arr[j]] = [arr[j], arr[i]]; }
  };
  shuffle();
  if (kind === 'acc') while (!missSpreadOk(arr, positives)) shuffle();
  return arr[idx] < positives;
}

export function accBagSatisfiable(hits: number, size: number): boolean {
  const misses = size - hits;
  if (misses <= 0) return true;
  if (hits <= 0) return false;
  for (let mask = 0; mask < 1 << size; mask++) {
    let set = 0;
    for (let i = 0; i < size; i++) if (mask & (1 << i)) set++;
    if (set !== misses) continue;
    const arr = Array.from({ length: size }, (_, i) => (mask & (1 << i) ? hits : 0));
    if (missSpreadOk(arr, hits)) return true;
  }
  return false;
}

export function assertAccBagSatisfiable(hits: number, size: number, label: string): void {
  if (!accBagSatisfiable(hits, size)) {
    throw new Error(
      `battle-sim: ${label} accuracy bag { hits: ${hits}, size: ${size} } admits no miss layout ` +
        `without a ≥3-miss run (need size ≤ 3·hits) — bagRoll's reject loop would never terminate. ` +
        `Raise hits or lower size.`,
    );
  }
}

for (const [cls, w] of Object.entries(WEAPONS)) {
  assertAccBagSatisfiable(w.accuracyBag.hits, w.accuracyBag.size, cls);
}

export interface RoundFighter extends FighterBase {
  weapon: WeaponClass;
  hp: number;
  alive: boolean;
  shotRolls: number;
  critRolls: number;
  stance: Stance;
  shieldedUntil?: number;
  shieldedBy?: string;
  blindedUntil?: number;
}

export interface RoundState {
  round: number;
  fighters: RoundFighter[];
}

export type ActionKind = 'shoot' | 'knife' | 'grenade' | 'medkit' | 'smoke' | 'flash';
export type DeathCause = 'gun' | 'knife' | 'grenade';

export interface Action {
  kind: ActionKind;
  stance?: Stance;
  shootTargetId?: string | null;
  grenadeTargetId?: string | null;
  smokeTargetId?: string | null;
  flashTargetId?: string | null;
}

export type RoundEvent =
  | { t: number; type: 'grenade'; shooter: string; target: string; hits: { id: string; damage: number; armor: number; absorbed?: number }[] }
  | { t: number; type: 'shot'; shooter: string; target: string; hit: boolean; crit: boolean; damage: number; armor: number; stance: Stance; reflect?: number; shield?: boolean;
      utilityTo?: string; utilityAmt?: number;
      absorbed?: number;
      reflectAbsorbed?: number;
      reflectArmor?: number;
      targetStance?: Stance;
      blind?: true;
      screened?: true }
  | { t: number; type: 'heal'; id: string; amount: number }
  | { t: number; type: 'shield'; id: string; amount: number }
  | { t: number; type: 'knife'; shooter: string; target: string; damage: number; armor: number; killed: boolean; punish?: number;
      absorbed?: number; punishAbsorbed?: number;
      punishArmor?: number }
  | { t: number; type: 'smoke'; shooter: string; target: string }
  | { t: number; type: 'flash'; shooter: string; targets: string[] }
  | { t: number; type: 'death'; id: string; by: string | null; headshot: boolean; cause?: DeathCause };

export interface RoundResult {
  events: RoundEvent[];
  state: RoundState;
  over: boolean;
  winner: Team | null;
  survivors: string[];
}

export interface RoundStats {
  damageBy: Record<string, number>;
  killsBy: Record<string, number>;
  utilityBy: Record<string, number>;
}

export function accumulateStats(events: RoundEvent[], prev?: RoundStats): RoundStats {
  const damageBy = { ...(prev?.damageBy ?? {}) };
  const killsBy = { ...(prev?.killsBy ?? {}) };
  const utilityBy = { ...(prev?.utilityBy ?? {}) };
  const credit = (id: string, amt: number) => { utilityBy[id] = (utilityBy[id] ?? 0) + amt; };
  for (const ev of events) {
    if (ev.type === 'shot') {
      if (ev.hit) damageBy[ev.shooter] = (damageBy[ev.shooter] ?? 0) + ev.damage;
      if (ev.utilityTo && ev.utilityAmt) credit(ev.utilityTo, ev.utilityAmt);
    } else if (ev.type === 'knife') {
      damageBy[ev.shooter] = (damageBy[ev.shooter] ?? 0) + ev.damage;
    } else if (ev.type === 'grenade') {
      const dealt = ev.hits.reduce((a, h) => a + h.damage, 0);
      damageBy[ev.shooter] = (damageBy[ev.shooter] ?? 0) + dealt;
    } else if (ev.type === 'heal') {
      credit(ev.id, ev.amount);
    } else if (ev.type === 'death' && ev.by) {
      killsBy[ev.by] = (killsBy[ev.by] ?? 0) + 1;
    }
  }
  return { damageBy, killsBy, utilityBy };
}

const ROW_GAP = 230;
function rowsFor(n: number): number[] {
  const center = (Y_TOP + Y_BOTTOM) / 2;
  if (n <= 1) return [center];
  const gap = Math.min(ROW_GAP, (Y_BOTTOM - Y_TOP) / (n - 1));
  const rows: number[] = [];
  for (let i = 0; i < n; i++) rows.push(center + (i - (n - 1) / 2) * gap);
  return rows;
}

export function fighterLayout(team: Team, index: number, teamCount: number): { y: number; targetX: number } {
  return {
    y: rowsFor(teamCount)[index],
    targetX: team === 't' ? 224 - (index % 2) * 22 : 800 + (index % 2) * 22,
  };
}

const NAMES: Record<Team, string[]> = {
  t: ['Shadow', 'Viper', 'Razor', 'Nova', 'Ghost'],
  ct: ['Blaze', 'Storm', 'Pulse', 'Knight', 'Zenith'],
};

export interface SeatMeta {
  name?: string;
  skin?: string | null;
  weaponSkins?: Record<string, string>;
}

export interface BuildStateOptions {
  meta?: Partial<Record<Team, SeatMeta[]>>;
}

export function buildInitialState(
  teamA: Loadout[],
  teamB: Loadout[],
  opts: BuildStateOptions = {},
): { state: RoundState; fighters: FighterInit[] } {
  const fighters: RoundFighter[] = [];
  const inits: FighterInit[] = [];

  const build = (loadouts: Loadout[], team: Team) => {
    loadouts.forEach((l, i) => {
      const id = `${team}${i}`;
      const maxHp = maxHpOf(l);
      const armor = armorPointsOf(l);
      const seat = opts.meta?.[team]?.[i];
      const name = seat?.name ?? NAMES[team][i % NAMES[team].length];
      const skin = seat?.skin ?? undefined;
      const weaponSkins = seat?.weaponSkins;
      fighters.push({
        id, team, weapon: l.weapon,
        hp: maxHp, maxHp, armor, stance: 'attack',
        hasGrenade: l.grenade, hasSmoke: !!l.smoke, hasMedkit: !!l.medkit, hasFlash: !!l.flash, alive: true,
        shotRolls: 0, critRolls: 0,
      });
      inits.push({ id, team, index: i, name, skin: skin ?? undefined, weaponSkins, weapon: l.weapon });
    });
  };
  build(teamA, 't');
  build(teamB, 'ct');

  return { state: { round: 0, fighters }, fighters: inits };
}

export function resolveRound(
  seed: string,
  state: RoundState,
  actionsById: Record<string, Action>,
): RoundResult {
  const rng = makeRng(`${seed}:r${state.round}`);

  // WHY: `?? 0` — a live state carried from before v30 has no bag cursors
  const fighters: RoundFighter[] = state.fighters.map((f) => ({ ...f, shotRolls: f.shotRolls ?? 0, critRolls: f.critRolls ?? 0 }));
  const ordered = [...fighters].sort((a, b) => (a.id < b.id ? -1 : 1));
  const aliveEnemies = (team: Team) => ordered.filter((f) => f.team !== team && f.hp > 0);
  const aliveOf = (team: Team) => ordered.reduce((n, f) => n + (f.team === team && f.hp > 0 ? 1 : 0), 0);
  const events: RoundEvent[] = [];

  const adrenaline: Record<string, number> = {};
  {
    const at = aliveOf('t');
    const act = aliveOf('ct');
    const trailing: Team | null = at < act ? 't' : act < at ? 'ct' : null;
    if (trailing) {
      const leaders = aliveEnemies(trailing);
      const leadHp = leaders.reduce((s, f) => s + f.hp, 0);
      const leadMaxHp = leaders.reduce((s, f) => s + f.maxHp, 0);
      const leadStrength = leadMaxHp > 0 ? leadHp / leadMaxHp : 0;
      const hpMult = COMEBACK_HP_FLOOR + (1 - COMEBACK_HP_FLOOR) * leadStrength;
      const frac = Math.min(ADRENALINE_MAX_FRAC, ADRENALINE_PER_GAP * Math.abs(at - act)) * hpMult;
      for (const f of fighters) {
        if (f.team !== trailing || f.hp <= 0) continue;
        const amount = Math.round(frac * f.maxHp);
        if (amount <= 0) continue;
        adrenaline[f.id] = amount;
        events.push({ t: 0, type: 'shield', id: f.id, amount });
      }
    }
  }

  // WHY: action + firing order is seeded-shuffled — walking sorted `ordered` gave team T the first strike in every
  // mutual-knife exchange
  const fireOrder = [...ordered];
  for (let i = fireOrder.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [fireOrder[i], fireOrder[j]] = [fireOrder[j], fireOrder[i]];
  }

  const segments = MIN_SEGMENTS + rng.int(MAX_SEGMENTS - MIN_SEGMENTS + 1);
  const roundTicks = segments * SEG_TICKS;

  const actionOf = (id: string): Action => actionsById[id] ?? { kind: 'shoot', stance: 'attack' };
  const stanceOf = (id: string): Stance => actionOf(id).stance ?? 'attack';
  const isShooter = (id: string): boolean => actionOf(id).kind === 'shoot';

  const applyDamage = (target: RoundFighter, base: number, by: string, crit: boolean, t: number, ignoreArmor = false, cause: DeathCause = 'gun'): { dealt: number; absorbed: number } => {
    let toHp = base;
    if (!ignoreArmor && target.armor > 0) {
      toHp = base * (1 - ARMOR_HP_REDUCTION * (crit ? 1 - CRIT_ARMOR_PIERCE : 1));
      target.armor = Math.max(0, Math.round(target.armor - (base * ARMOR_DEGRADE + ARMOR_DEGRADE_FLAT)));
    }
    const dmg = Math.max(1, Math.round(toHp));
    const absorbed = Math.min(adrenaline[target.id] ?? 0, dmg);
    if (absorbed > 0) adrenaline[target.id] -= absorbed;
    target.hp -= dmg - absorbed;
    if (target.hp <= 0) {
      target.hp = 0;
      target.alive = false;
      events.push({ t, type: 'death', id: target.id, by, headshot: crit, cause });
    }
    return { dealt: dmg, absorbed };
  };

  const pickTarget = (actor: RoundFighter, preferredId?: string | null): RoundFighter | null => {
    const enemies = aliveEnemies(actor.team);
    if (!enemies.length) return null;
    if (preferredId) {
      const pref = enemies.find((e) => e.id === preferredId);
      if (pref) return pref;
    }
    return enemies[rng.int(enemies.length)];
  };

  const blinded = new Set<string>();
  const blindedBy: Record<string, string> = {};
  const shielded = new Set<string>();
  for (const f of fighters) {
    if (f.shieldedUntil != null && f.shieldedUntil >= state.round) shielded.add(f.id);
    if (f.blindedUntil != null && f.blindedUntil >= state.round) blinded.add(f.id);
  }
  const nonShooters = new Set<string>();
  const pendingBlasts: { at: number; thrower: string; team: Team; targetId: string }[] = [];
  const pendingKnives: { thrower: RoundFighter; targetId: string }[] = [];

  for (const actor of fireOrder) {
    if (actor.hp <= 0) continue;
    const action = actionOf(actor.id);

    if (action.kind === 'medkit' && actor.hasMedkit) {
      actor.hasMedkit = false;
      const miss = actor.maxHp - actor.hp;
      const heal = Math.min(miss, Math.max(MEDKIT_HEAL_FLOOR, Math.round(MEDKIT_HEAL_FRAC * miss)));
      actor.hp += heal;
      events.push({ t: 0, type: 'heal', id: actor.id, amount: heal });
      nonShooters.add(actor.id);
      continue;
    } else if (action.kind === 'smoke' && actor.hasSmoke) {
      actor.hasSmoke = false;
      const mates = ordered.filter((f) => f.team === actor.team && f.hp > 0 && f.id !== actor.id);
      const byId = action.smokeTargetId
        ? mates.find((f) => f.id === action.smokeTargetId && f.alive)
        : null;
      const tf = byId ?? (mates.length ? mates.reduce((m, f) => (f.hp / f.maxHp < m.hp / m.maxHp ? f : m)) : null);
      if (tf) {
        shielded.add(tf.id);
        tf.shieldedUntil = state.round + SMOKE_SHIELD_ROUNDS - 1;
        tf.shieldedBy = actor.id;
        events.push({ t: 0, type: 'smoke', shooter: actor.id, target: tf.id });
      }
      nonShooters.add(actor.id);
      continue;
    } else if (action.kind === 'knife') {
      const target = pickTarget(actor, action.shootTargetId);
      if (target) pendingKnives.push({ thrower: actor, targetId: target.id });
      nonShooters.add(actor.id);
      continue;
    } else if (action.kind === 'grenade' && actor.hasGrenade) {
      const center = pickTarget(actor, action.grenadeTargetId);
      if (center) {
        actor.hasGrenade = false;
        pendingBlasts.push({ at: GRENADE_FLIGHT_TICKS, thrower: actor.id, team: actor.team, targetId: center.id });
        nonShooters.add(actor.id);
        continue;
      }
    } else if (action.kind === 'flash' && actor.hasFlash) {
      actor.hasFlash = false;
      const center = pickTarget(actor, action.flashTargetId);
      if (center) {
        const rest = aliveEnemies(actor.team).filter((e) => e.id !== center.id).slice(0, FLASH_SPLASH_MAX);
        const targets = [center, ...rest];
        for (const e of targets) { blinded.add(e.id); blindedBy[e.id] = actor.id; e.blindedUntil = state.round + FLASH_BLIND_ROUNDS - 1; }
        events.push({ t: 0, type: 'flash', shooter: actor.id, targets: targets.map((e) => e.id) });
      }
      nonShooters.add(actor.id);
      continue;
    }

    actor.stance = stanceOf(actor.id);
  }

  const nextShot: Record<string, number> = {};
  // WHY: clamp the first-shot offset to the round — AWP fireTicks (130) exceeds a short round (~112), so the AWP never fired
  for (const f of ordered) nextShot[f.id] = rng.int(Math.min(WEAPONS[f.weapon].fireTicks, roundTicks + 1));

  const shotTarget: Record<string, string> = {};

  const knivesLeft = [...pendingKnives];

  let winner: Team | null = null;
  for (let t = 0; t <= roundTicks; t++) {
    const tA = aliveEnemies('ct');
    const tB = aliveEnemies('t');
    if (!tA.length || !tB.length) {
      winner = tA.length ? 't' : tB.length ? 'ct' : null;
      break;
    }

    if (t >= KNIFE.tick && knivesLeft.length) {
      // WHY: mutual lethal knife pairs resolve simultaneously — sequential resolution lets the first-processed strike kill
      // before the other swings
      const knifeHit = (E: RoundFighter) => {
        const effHp = E.hp + (adrenaline[E.id] ?? 0);
        const execute = effHp <= E.maxHp * KNIFE.executeHpFrac;
        const base = execute ? effHp : KNIFE.damage;
        return { execute, base, lethal: Math.max(1, Math.round(base)) >= effHp };
      };
      const resolvedPairs = new Set<number>();
      for (let i = 0; i < knivesLeft.length; i++) {
        if (resolvedPairs.has(i)) continue;
        const a = knivesLeft[i];
        if (a.thrower.hp <= 0) continue;
        const j = knivesLeft.findIndex(
          (b, idx) => idx !== i && !resolvedPairs.has(idx) && b.thrower.id === a.targetId && b.targetId === a.thrower.id,
        );
        if (j === -1) continue;
        const b = knivesLeft[j];
        const A = a.thrower;
        const B = b.thrower;
        if (B.hp <= 0) continue;
        const hitA = knifeHit(A);
        const hitB = knifeHit(B);
        if (!hitA.lethal || !hitB.lethal) continue;
        resolvedPairs.add(i);
        resolvedPairs.add(j);
        const rB = applyDamage(B, hitB.base, A.id, hitB.execute, t, true, 'knife');
        events.push({ t, type: 'knife', shooter: A.id, target: B.id, damage: rB.dealt, armor: B.armor, killed: !B.alive, ...(rB.absorbed && { absorbed: rB.absorbed }) });
        const rA = applyDamage(A, hitA.base, B.id, hitA.execute, t, true, 'knife');
        events.push({ t, type: 'knife', shooter: B.id, target: A.id, damage: rA.dealt, armor: A.armor, killed: !A.alive, ...(rA.absorbed && { absorbed: rA.absorbed }) });
      }
      if (resolvedPairs.size) {
        for (let i = knivesLeft.length - 1; i >= 0; i--) if (resolvedPairs.has(i)) knivesLeft.splice(i, 1);
      }

      for (let i = knivesLeft.length - 1; i >= 0; i--) {
        const k = knivesLeft[i];
        knivesLeft.splice(i, 1);
        const K = k.thrower;
        if (K.hp <= 0) continue;
        const E = fighters.find((f) => f.id === k.targetId);
        if (!E || E.hp <= 0) continue;
        const targetActive = isShooter(E.id) && stanceOf(E.id) !== 'defense';
        if (targetActive) {
          const hit = applyDamage(E, KNIFE.weak, K.id, false, t, true, 'knife');
          const knifeEv: Extract<RoundEvent, { type: 'knife' }> = { t, type: 'knife', shooter: K.id, target: E.id, damage: hit.dealt, armor: E.armor, killed: !E.alive, ...(hit.absorbed && { absorbed: hit.absorbed }) };
          events.push(knifeEv);
          if (E.alive) {
            const punish = applyDamage(K, WEAPONS[E.weapon].damage * KNIFE.punishMult, E.id, false, t);
            knifeEv.punish = punish.dealt;
            if (punish.absorbed) knifeEv.punishAbsorbed = punish.absorbed;
            knifeEv.punishArmor = K.armor;
          }
        } else {
          const effHp = E.hp + (adrenaline[E.id] ?? 0);
          const execute = effHp <= E.maxHp * KNIFE.executeHpFrac;
          const hit = applyDamage(E, execute ? effHp : KNIFE.damage, K.id, execute, t, true, 'knife');
          events.push({ t, type: 'knife', shooter: K.id, target: E.id, damage: hit.dealt, armor: E.armor, killed: !E.alive, ...(hit.absorbed && { absorbed: hit.absorbed }) });
        }
      }
    }

    for (let i = pendingBlasts.length - 1; i >= 0; i--) {
      const b = pendingBlasts[i];
      if (t < b.at) continue;
      pendingBlasts.splice(i, 1);
      const factor = grenadeScatter(rng.range(0, 1));
      const all = aliveEnemies(b.team);
      const target = all.find((e) => e.id === b.targetId);
      const splashed = all.filter((e) => e.id !== b.targetId).slice(0, GRENADE_SPLASH_MAX);
      const victims = target ? [target, ...splashed] : splashed;
      const hits = victims.map((e) => {
        const isCenter = e.id === b.targetId;
        let base = isCenter ? GRENADE_DAMAGE : GRENADE_DAMAGE * GRENADE_SPLASH;
        if (isCenter && isShooter(e.id)) base *= GRENADE_VS_STANCE[stanceOf(e.id)];
        const hit = applyDamage(e, base * factor, b.thrower, false, b.at, isCenter, 'grenade');
        return { id: e.id, damage: hit.dealt, armor: e.armor, ...(hit.absorbed && { absorbed: hit.absorbed }) };
      });
      events.push({ t: b.at, type: 'grenade', shooter: b.thrower, target: b.targetId, hits });
    }

    for (const s of fireOrder) {
      if (s.hp <= 0 || nonShooters.has(s.id)) continue;
      const w = WEAPONS[s.weapon];
      const ss = stanceOf(s.id);
      const chosen = actionsById[s.id]?.shootTargetId;
      while (nextShot[s.id] <= t && s.hp > 0) {
        const locked = shotTarget[s.id]
          ? fighters.find((f) => f.id === shotTarget[s.id] && f.alive && f.team !== s.team)
          : undefined;
        const target = locked ?? pickTarget(s, chosen);
        if (!target) break;
        shotTarget[s.id] = target.id;
        const ts = isShooter(target.id) ? stanceOf(target.id) : null;
        // WHY: fixed order bag → evasion → blind; main RNG is spent only when a modifier applies, so the bag never shifts the
        // round stream
        const bag = w.accuracyBag;
        const hitBase = bagRoll(seed, s.id, 'acc', s.shotRolls++, bag.hits, bag.size);
        const dodged = hitBase && ts === 'maneuver' && !rng.chance(STANCE.maneuver.evasion);
        const blindMiss = hitBase && !dodged && blinded.has(s.id) && !rng.chance(FLASH_ACCURACY);
        const hit = hitBase && !dodged && !blindMiss;
        let crit = false;
        let damage = 0;
        let absorbed = 0;
        let shield = false;
        if (hit) {
          const cb = w.critBag;
          crit = !blinded.has(s.id) && bagRoll(seed, s.id, 'crit', s.critRolls++, cb.crits, cb.size);
          const mitigation = ts === 'defense' ? STANCE.defense.mitigation : 1;
          let focusMit = 1;
          if ((adrenaline[target.id] ?? 0) <= 0) {
            const tn = aliveOf(target.team);
            const en = aliveOf(target.team === 't' ? 'ct' : 't');
            if (en >= tn) {
              let fc = 0;
              for (const f of ordered) if (f.team !== target.team && f.hp > 0 && shotTarget[f.id] === target.id) fc++;
              const over = fc - (en > tn ? 1 : 2);
              if (over > 0) focusMit = 1 - Math.min(FOCUS_RESIST_CAP, FOCUS_RESIST_K * over);
            }
          }
          shield = focusMit < 1;
          const smokeFactor = shielded.has(target.id) ? SMOKE_MIT : 1;
          const raw = w.damage * (crit ? w.critMult : 1) * STANCE[ss].outgoing * mitigation * focusMit * smokeFactor;
          const dealtHit = applyDamage(target, raw, s.id, crit, t);
          damage = dealtHit.dealt;
          absorbed = dealtHit.absorbed;
        }
        let reflect: number | undefined;
        let reflectAbsorbed: number | undefined;
        let reflectArmor: number | undefined;
        if (ts === 'defense' && ss === 'attack' && damage > 0 && target.alive && !shielded.has(s.id)) {
          const back = applyDamage(s, damage * STANCE.defense.reflect, target.id, false, t);
          reflect = back.dealt;
          if (back.absorbed) reflectAbsorbed = back.absorbed;
          reflectArmor = s.armor;
        }
        const flasher = blinded.has(s.id) ? blindedBy[s.id] : undefined;
        const denied = flasher ? Math.round(w.damage * w.accuracy * (1 - FLASH_ACCURACY) * STANCE[ss].outgoing) : 0;
        const smoker = hit && shielded.has(target.id) ? target.shieldedBy : undefined;
        const prevented = smoker ? Math.round(w.damage * STANCE[ss].outgoing * (1 - SMOKE_MIT)) : 0;
        const utilityCredit = smoker && prevented
          ? { utilityTo: smoker, utilityAmt: prevented }
          : flasher && denied
            ? { utilityTo: flasher, utilityAmt: denied }
            : {};
        events.push({ t, type: 'shot', shooter: s.id, target: target.id, hit, crit, damage, armor: target.armor, stance: ss, reflect, ...(shield && { shield: true }),
          ...(absorbed && { absorbed }), ...(reflectAbsorbed && { reflectAbsorbed }),
          ...(reflectArmor !== undefined && { reflectArmor }),
          ...(ts && { targetStance: ts }),
          ...(blinded.has(s.id) && { blind: true as const }),
          ...(shielded.has(target.id) && { screened: true as const }),
          ...utilityCredit });
        nextShot[s.id] += w.fireTicks;
      }
    }
  }

  const aliveT = fighters.filter((f) => f.team === 't' && f.alive);
  const aliveCt = fighters.filter((f) => f.team === 'ct' && f.alive);
  const over = aliveT.length === 0 || aliveCt.length === 0;
  if (over) winner = aliveT.length ? 't' : aliveCt.length ? 'ct' : null;
  else winner = null;
  const survivors = winner ? fighters.filter((f) => f.team === winner && f.alive).map((f) => f.id) : [];

  events.sort((a, b) => a.t - b.t);

  return {
    events,
    state: { round: state.round + 1, fighters },
    over,
    winner,
    survivors,
  };
}
